/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, {useEffect, useState} from "react";
// nodejs library that concatenates classes

// reactstrap components
import {
  Button,
  Badge,
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Spinner,
  Table,
} from "reactstrap";

// core components

// index page sections


import {RiFileListLine, RiFolderMusicLine, RiQuestionLine, RiTeamLine, RiUserLine} from "react-icons/ri";
import YourDetailsForm from "./YourDetailsForm";
import YourRecentPlaylists from "./YourRecentPlaylists";
import withNavigateHook from "../../hooks/withNavigationHook";
import {useParams} from "react-router-dom";
import {sessionEnded} from "./SessionEnded";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import Api from "../../components/helpers/Api";

const subscriptionBadgeColor = (status) => {
  if (status === "active") return "success";
  if (status === "trialing") return "info";
  if (status === "past_due") return "warning";
  return "danger";
};

const roleBadgeColor = (role) => {
  if (role === "OWNER") return "primary";
  if (role === "ADMIN") return "info";
  return "secondary";
};

const GroupAccountSummary = () => {
  const customer = JSON.parse(sessionStorage.getItem("customer"));
  const [account, setAccount] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noAccount, setNoAccount] = useState(false);
  const [error, setError] = useState("");
  const role = account ? (customer?.user?.accountRole ?? null) : null;
  const canManage = role === "OWNER" || role === "ADMIN";
  const canManageBilling = role === "OWNER" || (role === "ADMIN" && account?.accountType !== "ENTERPRISE");
  const subscriptionActive = account?.subscriptionStatus === "active" || account?.subscriptionStatus === "trialing";

  useEffect(() => {
    const loadAccount = async () => {
      try {
        const [accountResponse, membersResponse] = await Promise.all([
          Api.get("/api/account/me"),
          Api.get("/api/account/members"),
        ]);
        setAccount(accountResponse.data);
        setMembers(membersResponse.data);
      } catch (e) {
        if (e.response?.status === 404) {
          setNoAccount(true);
        } else {
          setError("Unable to load your group details.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="py-4 text-center"><Spinner color="primary" /></div>;
  }

  if (noAccount) {
    return (
      <>
        <p className="description mt-3">
          Create a group account to invite others and plan worship services together.
        </p>
        <Button className="mt-4" color="primary" href="/your-profile/team/upgrade">
          Register a group
        </Button>
      </>
    );
  }

  if (error) {
    return <div className="alert alert-warning mt-3">{error}</div>;
  }

  return (
    <>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mt-3">
        <div>
          <h4 className="mb-2">{account?.name}</h4>
          <Badge color={roleBadgeColor(role)} style={{letterSpacing: 0}}>{role}</Badge>
        </div>
        <Button className="mt-3 mt-md-0" color="primary" href="/your-profile/team">
          {canManage ? "Manage your group" : "View your group"}
        </Button>
      </div>

      {canManage && (
        <div className="mt-3">
          <p className="description mb-2">
            Subscription{" "}
            <Badge color={subscriptionBadgeColor(account?.subscriptionStatus)}>
              {account?.subscriptionStatus || "not active"}
            </Badge>
          </p>
          <p className="description mb-2">
            Seats: {account?.activeSeats ?? account?.memberCount}{account?.accountType === "ENTERPRISE" ? " seats (no limit)" : ` of ${account?.maxSeats ?? 10} seats used`}
          </p>
          {!subscriptionActive && (
            <div className="alert alert-warning mt-3 mb-0">
              Your {account?.accountType === "ENTERPRISE" ? "Organisation admin" : "Group"} subscription is inactive. You cannot invite new members until your subscription is renewed.{" "}
              {canManageBilling && <a href="/your-profile/billing-details" className="alert-link">Renew subscription</a>}
            </div>
          )}
        </div>
      )}

      <Table responsive className="mt-4 mb-0">
        <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Role</th>
        </tr>
        </thead>
        <tbody>
        {[...members].sort((a, b) => {
          const nameA = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim().toLowerCase();
          const nameB = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim().toLowerCase();
          if (nameA !== nameB) return nameA.localeCompare(nameB);
          return (a.email ?? "").toLowerCase().localeCompare((b.email ?? "").toLowerCase());
        }).map((member) => (
          <tr key={member.userId}>
            <td>{member.firstName} {member.lastName}</td>
            <td>{member.email}</td>
            <td><Badge color={roleBadgeColor(member.role)} style={{letterSpacing: 0}}>{member.role}</Badge></td>
          </tr>
        ))}
        </tbody>
      </Table>
    </>
  );
};

class YourProfile extends React.Component {
  state = {
    songBookCount: null
  };

  render() {
    if (!sessionStorage.getItem("customer")) {
      return sessionEnded();
    }

    return (
      <>
        <SeoNoIndex noindex={true} />

        {/*<main ref="main">*/}
          <div className="position-relative">
            {/* shape Hero */}
            <section className="section section-lg section-shaped pb-250">
              <div className="shape shape-style-1 shape-default">
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
                <span/>
              </div>
              <Container className="py-lg-md d-flex">
                <div className="col px-0">
                  <Row>
                    <Col lg="8">
                      <h1 className="display-3 text-white">
                        Building your song-flows{" "}
                        <span>your profile</span>
                      </h1>
                      {/*<p className="lead text-white">*/}
                      {/*  The design system comes with four pre-built pages to*/}
                      {/*  help you get started faster. You can change the text and*/}
                      {/*  images and you're good to go.*/}
                      {/*</p>*/}
                    </Col>
                  </Row>
                </div>
              </Container>
              {/* SVG separator */}
              <div className="separator separator-bottom separator-skew">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    version="1.1"
                    viewBox="0 0 2560 100"
                    x="0"
                    y="0"
                >
                  <polygon
                      className="fill-white"
                      points="2560 0 2560 100 0 100"
                  />
                </svg>
              </div>
            </section>
            {/* 1st Hero Variation */}
          </div>
          <section className="section section-lg pt-lg-0 mt--200">
            <Container>
              <Row className="justify-content-center">
                <Col lg="12">
                  <Row className="row-grid">
                    <Col lg="8">
                      <Card className="card shadow border-0">
                        <CardBody className="py-5">
                          {/*<div className="icon icon-shape icon-shape-primary rounded-circle mb-4">*/}
                          {/*  <i className="ni ni-check-bold" />*/}
                          {/*</div>*/}
                          <h2 style={{fontSize: '1rem'}} className="text-primary text-uppercase">
                            <RiUserLine color='#5e72e4' size="36"/>&nbsp;Your details
                          </h2>
                          <p className="description mt-3">
                            Manage your personal details
                          </p>
                          <p className="description mt-3">
                            <YourDetailsForm/>
                          </p>
                          {/*<Button*/}
                          {/*    className="mt-4"*/}
                          {/*    color="primary"*/}
                          {/*    href="/"*/}
                          {/*>*/}
                          {/*  Update your details*/}
                          {/*</Button>*/}
                          <Row>
                            <p className="description mt-3" style={{paddingLeft: 15 + "px"}}>
                              <a href="/password/reset/request">Request a password reset email</a>
                            </p>
                          </Row>
                          <Row>
                            {!JSON.parse(sessionStorage.getItem("customer")).user.verified &&
                                <>
                                  <p className="description mt-3 text-warning" style={{paddingLeft: 15 + "px"}}>
                                    <a href="/email/verify/request" className="text-warning">Please verify your email
                                      address. Click here to request a new verification email</a>
                                  </p>
                                </>
                            }
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col lg="4">
                      <Card className="card shadow border-0">
                        <CardBody className="py-5">
                          {/*<div className="icon icon-shape icon-shape-success rounded-circle mb-4">*/}
                          {/*  <i className="ni ni-istanbul" />*/}
                          {/*</div>*/}
                          <Row>
                            <Col>
                              <h2 style={{fontSize: '1rem'}} className="text-primary text-uppercase">
                                <RiFolderMusicLine color='#5e72e4' size="36"/>&nbsp;Your saved song-flows
                              </h2>
                            </Col>
                          </Row>
                          <p className="description mt-3">
                            <YourRecentPlaylists/>
                          </p>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  <Row className="row-grid">
                    <Col lg="12">
                      <Card className="card shadow border-0">
                        <CardBody className="py-5">
                          <Row>
                            <Col>
                              <h2 style={{fontSize: '1rem'}} className="text-primary text-uppercase">
                                <RiFileListLine color='#5e72e4' size="36" />&nbsp;My service plan templates
                              </h2>
                            </Col>
                          </Row>
                          <p className="description mt-3">
                            Create and manage your reusable service plan templates.
                          </p>
                          <Button className="mt-4" color="primary" href="/my/templates">
                            View my templates
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  {/*<Row className="row-grid">*/}
                  {/*  <Col lg="12">*/}
                  {/*    <Card className="card shadow border-0">*/}
                  {/*      <CardBody className="py-5">*/}
                  {/*        <h2 style={{fontSize: '1rem'}} className="text-primary text-uppercase">*/}
                  {/*          <RiTeamLine color='#5e72e4' size="36" />&nbsp;Your group*/}
                  {/*        </h2>*/}
                  {/*        <p className="description mt-3">*/}
                  {/*          Manage your group, members, and invitations.*/}
                  {/*        </p>*/}
                  {/*        <GroupAccountSummary />*/}
                  {/*      </CardBody>*/}
                  {/*    </Card>*/}
                  {/*  </Col>*/}
                  {/*</Row>*/}
                  <Row className="row-grid">
                    <Col lg="12">
                      <Card className="card shadow border-0">
                        <CardBody className="py-5">
                          {/*<div className="icon icon-shape icon-shape-warning rounded-circle mb-4">*/}
                          {/*  <i className="ni ni-planet" />*/}
                          {/*</div>*/}
                          <Row>
                            <Col>
                              <h2 style={{fontSize: '1rem'}} className="text-primary text-uppercase">
                                <RiQuestionLine color='#5e72e4' size="36"/>&nbsp;Help
                              </h2>
                            </Col>
                          </Row>
                          <p className="description mt-3">
                            You and your music team can build, save and edit unlimited curated song-flows using all of our
                            {" "}{!sessionStorage.getItem("statsTtl") || JSON.parse(sessionStorage.getItem("statsTtl")).stats.find(x => x.title === "Song Books").value === 0 ? "" : JSON.parse(sessionStorage.getItem("statsTtl")).stats.find(x => x.title === "Song Books").value}{" "}
                            song books.
                          </p>
                          <Button
                              className="mt-4"
                              color="primary"
                              href="/help"
                          >
                            View all help
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </section>
        {/*</main>*/}
        
      </>
    );
  }
}

export default withNavigateHook((props) => (
    <YourProfile{...props} params={useParams()} />
));
